import { Injectable, OnDestroy } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  isSupported,
} from 'firebase/messaging';
import { environment } from '../environments/environments';
import { INotifications } from '../state-management/states/aumye-dashboard.state';
import { IClientNotifications } from './realTimeData/aumye-firebase-realtime-db.service';
import { Store } from '@ngrx/store';
import { IAumyeState } from '../state-management/states/aumye.state';
import * as fromDashboardActions from 'src/app/state-management/actions/dashboard.action';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService implements OnDestroy {
  private messaging: Messaging | null = null;
  private firebaseApp: FirebaseApp | null = null;
  private readonly VAPID_PUBLIC_KEY: string;
  private readonly destroy$ = new Subject<void>();
  private unsubscribeMessage: (() => void) | null = null;
  private isInitialized = false;
  private readonly STORAGE_KEY = 'userNotifications';
  private readonly TOKEN_KEY = 'Web_Token';
  private readonly DB_NAME = 'notifications-db';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'notifications';
  private readonly platform = Capacitor.getPlatform();

  constructor(private store: Store<IAumyeState>, private router: Router) {
    this.VAPID_PUBLIC_KEY =
      'BJlK1xdB5Fq1AELwDCFRy9YPCxXYpb1vExaSN2IIMyLtoW8adE-9OMrp3uKw-1vJNrc4CoRWZyjI8TurW456wb0';
  }

  /**
   * Initialize Firebase Messaging
   * Call this method after user login or app initialization
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('Firebase Messaging already initialized');
      return;
    }

    try {
      // Check if Firebase Messaging is supported
      const supported = await isSupported();
      if (!supported) {
        console.warn(
          'Firebase Messaging is not supported on this browser/platform'
        );
        return;
      }

      // Initialize Firebase App
      if (!this.firebaseApp) {
        this.firebaseApp = initializeApp(environment.firebaseConfig);
      }

      // Initialize Messaging (web only)
      if (!Capacitor.isNativePlatform() && this.platform === 'web') {
        this.messaging = getMessaging(this.firebaseApp);
        this.isInitialized = true;
        console.log('Firebase Messaging initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize Firebase Messaging:', error);
      throw error;
    }
  }

  /**
   * Register service worker for push notifications (web only)
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers not supported');
        return null;
      }

      // Check if already registered
      const existingRegistration =
        await navigator.serviceWorker.getRegistration(
          '/firebase-messaging-sw.js'
        );
      if (existingRegistration) {
        console.log('Service Worker already registered');
        return existingRegistration;
      }

      // Register new service worker
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        {
          scope: '/',
          updateViaCache: 'none',
        }
      );

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      console.log('Service Worker registered successfully:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Request notification permission and get FCM token
   * Works on web, Android, and iOS
   */
  async requestPermission(): Promise<string> {
    try {
      // Check platform
      if (Capacitor.isNativePlatform()) {
        // Mobile (Android/iOS) - Use Capacitor Push Notifications
        return await this.requestMobilePermission();
      } else {
        // Web platform
        return await this.requestWebPermission();
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      return '';
    }
  }

  /**
   * Request permission for web platform
   */
  private async requestWebPermission(): Promise<string> {
    try {
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return '';
      }

      // Check current permission status
      if (Notification.permission === 'denied') {
        console.warn('Notification permission previously denied');
        return '';
      }

      // Request permission if not already granted
      let permission: NotificationPermission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.warn('Notification permission not granted:', permission);
        return '';
      }

      // Ensure Firebase is initialized
      if (!this.isInitialized) {
        await this.initialize();
      }

      if (!this.messaging) {
        console.error('Firebase Messaging not initialized');
        return '';
      }

      // Register service worker
      const registration = await this.registerServiceWorker();
      if (!registration) {
        console.error('Service Worker registration failed');
        return '';
      }

      // Get FCM token
      const token = await getToken(this.messaging, {
        vapidKey: this.VAPID_PUBLIC_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        // console.log('FCM Token obtained:', token);
        await this.sendTokenToServer(token);
        return token;
      } else {
        console.warn('No FCM registration token available');
        return '';
      }
    } catch (error) {
      console.error('Error in web permission request:', error);
      return '';
    }
  }

  /**
   * Request permission for mobile platforms (Android/iOS)
   * Requires @capacitor/push-notifications plugin
   */
  private async requestMobilePermission(): Promise<string> {
    try {
      // Import Capacitor Push Notifications dynamically
      const { PushNotifications } = await import(
        '@capacitor/push-notifications'
      );

      // Request permission
      const permResult = await PushNotifications.requestPermissions();

      if (permResult.receive === 'granted') {
        console.log('Push notification permission granted');

        // Register for push notifications
        await PushNotifications.register();

        // Listen for registration
        return new Promise((resolve) => {
          PushNotifications.addListener('registration', async (token) => {
            console.log('Push registration success, token:', token.value);
            await this.sendTokenToServer(token.value);
            resolve(token.value);
          });

          PushNotifications.addListener('registrationError', (error) => {
            console.error('Push registration error:', error);
            resolve('');
          });

          // Timeout after 10 seconds
          setTimeout(() => resolve(''), 10000);
        });
      } else {
        console.warn('Push notification permission denied');
        return '';
      }
    } catch (error) {
      console.error('Error requesting mobile permission:', error);
      return '';
    }
  }

  /**
   * Listen to foreground notifications (web only)
   */
  async listenToNotifications(): Promise<void> {
    try {
      if (!this.messaging) {
        console.warn('Firebase Messaging not initialized');
        return;
      }

      console.log('Starting to listen for push notifications');

      // Unsubscribe from previous listener if exists
      if (this.unsubscribeMessage) {
        this.unsubscribeMessage();
      }

      // Listen for foreground messages
      this.unsubscribeMessage = onMessage(this.messaging, (payload) => {
        console.log('Foreground message received:', payload);
        this.handleNotificationPayload(payload);
      });

      // Sync any pending notifications from IndexedDB
      await this.syncNotificationsToLocalStorage();
    } catch (error) {
      console.error('Error setting up notification listener:', error);
    }
  }

  /**
   * Setup mobile push notification listeners
   */
  async setupMobileNotificationListeners(): Promise<void> {
    try {
      const { PushNotifications } = await import(
        '@capacitor/push-notifications'
      );

      // Notification received in foreground
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          console.log('Push notification received:', notification);
          this.handleMobileNotification(notification);
        }
      );

      // Notification action performed (user tapped on notification)
      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification) => {
          console.log('Push notification action performed:', notification);
          this.handleNotificationClick(notification.notification.data);
        }
      );
    } catch (error) {
      console.error('Error setting up mobile notification listeners:', error);
    }
  }

  /**
   * Handle notification payload from Firebase
   */
  private handleNotificationPayload(payload: any): void {
    try {
      if (payload?.data?.['notificationData']) {
        const parsedNotification: IClientNotifications = JSON.parse(
          payload.data['notificationData']
        );
        this.saveNotification(parsedNotification);
        this.showNotification(payload, parsedNotification);
      } else {
        console.warn('Invalid notification payload:', payload);
      }
    } catch (error) {
      console.error('Error handling notification payload:', error);
    }
  }

  /**
   * Handle mobile notification
   */
  private handleMobileNotification(notification: any): void {
    try {
      const notificationData = notification.data?.notificationData;
      if (notificationData) {
        const parsedNotification: IClientNotifications =
          typeof notificationData === 'string'
            ? JSON.parse(notificationData)
            : notificationData;
        this.saveNotification(parsedNotification);
      }
    } catch (error) {
      console.error('Error handling mobile notification:', error);
    }
  }

  /**
   * Save notification to storage and dispatch to store
   */
  saveNotification(message: IClientNotifications): void {
    try {
      const notification: INotifications = {
        notificationOrg: message.OrgId,
        notificationTitle: message.MessageTitle,
        notificationBody: message.MessageBody,
        notificationFlowType: message.FlowType,
        notificationId: message.Id,
        notificationPeopleId: this.parseMessageData(message.MessageData),
        notificationLink: message.MessageLink,
        timestamp: new Date(),
        read: false,
        type: 'INFO',
      };

      // Get existing notifications
      const existingNotifications = this.getNotificationsFromStorage();

      // Check for duplicates
      const isDuplicate = existingNotifications.some(
        (n) => n.notificationId === notification.notificationId
      );

      if (!isDuplicate) {
        // Add new notification at the beginning
        existingNotifications.unshift(notification);

        // Limit to last 100 notifications to prevent storage bloat
        const limitedNotifications = existingNotifications.slice(0, 100);

        // Save to localStorage
        this.saveNotificationsToStorage(limitedNotifications);

        // Dispatch to store
        this.store.dispatch(
          new fromDashboardActions.AddNewNotification(notification)
        );

        console.log(
          'Notification saved successfully:',
          notification.notificationId
        );
      } else {
        console.log(
          'Duplicate notification ignored:',
          notification.notificationId
        );
      }
    } catch (error) {
      console.error('Error saving notification:', error);
    }
  }

  /**
   * Parse message data safely
   */
  private parseMessageData(messageData?: string): any[] {
    try {
      return messageData ? JSON.parse(messageData) : [];
    } catch (error) {
      console.error('Error parsing message data:', error);
      return [];
    }
  }

  /**
   * Get notifications from localStorage
   */
  getNotificationsFromStorage(): INotifications[] {
    try {
      const storedNotifications = localStorage.getItem(this.STORAGE_KEY);
      return storedNotifications ? JSON.parse(storedNotifications) : [];
    } catch (error) {
      console.error('Error reading notifications from storage:', error);
      return [];
    }
  }

  /**
   * Save notifications to localStorage
   */
  private saveNotificationsToStorage(notifications: INotifications[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }

  /**
   * Sync notifications from IndexedDB to localStorage
   */
  async syncNotificationsToLocalStorage(): Promise<void> {
    return new Promise((resolve) => {
      try {
        const dbPromise = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        dbPromise.onupgradeneeded = (event: any) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME, {
              keyPath: 'id',
              autoIncrement: true,
            });
          }
        };

        dbPromise.onsuccess = (event: any) => {
          const db = event.target.result;

          try {
            const transaction = db.transaction(this.STORE_NAME, 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            const request = store.getAll();

            request.onsuccess = () => {
              const notifications = request.result.filter(
                (n: any) => n.needsSync
              );

              if (notifications.length > 0) {
                const existingNotifications =
                  this.getNotificationsFromStorage();

                notifications.forEach((notification: any) => {
                  // Check for duplicates
                  const isDuplicate = existingNotifications.some(
                    (n) => n.notificationId === notification.notificationId
                  );

                  if (!isDuplicate) {
                    existingNotifications.unshift(notification);
                  }

                  // Update sync flag in IndexedDB
                  store.put({ ...notification, needsSync: false });
                });

                // Limit and save
                const limitedNotifications = existingNotifications.slice(
                  0,
                  100
                );
                this.saveNotificationsToStorage(limitedNotifications);

                console.log(
                  `Synced ${notifications.length} notifications from IndexedDB`
                );
              }

              db.close();
              resolve();
            };

            request.onerror = () => {
              console.error('Error reading from IndexedDB:', request.error);
              db.close();
              resolve();
            };
          } catch (error) {
            console.error('Transaction error:', error);
            db.close();
            resolve();
          }
        };

        dbPromise.onerror = (event: any) => {
          console.error('IndexedDB error:', event.target.error);
          resolve();
        };
      } catch (error) {
        console.error('Error in syncNotificationsToLocalStorage:', error);
        resolve();
      }
    });
  }

  /**
   * Show browser notification (web only)
   */
  private showNotification(payload: any, message: IClientNotifications): void {
    try {
      if (
        !('Notification' in window) ||
        Notification.permission !== 'granted'
      ) {
        console.warn('Cannot show notification: permission not granted');
        return;
      }

      const clickUrl = message?.MessageLink || '/dashboard';
      const title =
        payload.notification?.title || message.MessageTitle || 'Notification';
      const options: NotificationOptions = {
        body: payload.notification?.body || message.MessageBody || '',
        icon: payload.notification?.icon || '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/badge-72x72.png',
        tag: message.Id || Date.now().toString(),
        requireInteraction: false,
        data: { url: clickUrl },
      };

      const notification = new Notification(title, options);

      notification.onclick = (event) => {
        event.preventDefault();
        notification.close();

        // Focus window
        if (window.parent) {
          window.parent.focus();
        }
        window.focus();

        // Navigate to URL
        this.handleNotificationClick({ url: clickUrl });
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Handle notification click navigation
   */
  private handleNotificationClick(data: any): void {
    try {
      const url = data?.url || data?.MessageLink || '/dashboard';
      console.log('Navigating to:', url);

      // Use Angular router for navigation
      this.router.navigateByUrl(url).catch((error) => {
        console.error('Navigation error:', error);
      });
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  }

  /**
   * Send token to backend server
   */
  private async sendTokenToServer(token: string): Promise<void> {
    try {
      // Save to localStorage
      localStorage.setItem(this.TOKEN_KEY, token);

      // TODO: Send token to your backend API
      // Example:
      // await this.http.post('/api/notifications/register-token', { token }).toPromise();

      // console.log('Token saved to storage:', token);
    } catch (error) {
      console.error('Error sending token to server:', error);
    }
  }

  /**
   * Get current FCM token
   */
  getCurrentToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    try {
      const notifications = this.getNotificationsFromStorage();
      const updated = notifications.map((n) =>
        n.notificationId === notificationId ? { ...n, read: true } : n
      );
      this.saveNotificationsToStorage(updated);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Delete notification
   */
  deleteNotification(notificationId: string): void {
    try {
      const notifications = this.getNotificationsFromStorage();
      const filtered = notifications.filter(
        (n) => n.notificationId !== notificationId
      );
      this.saveNotificationsToStorage(filtered);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.unsubscribeMessage) {
      this.unsubscribeMessage();
    }

    console.log('Firebase Messaging Service destroyed');
  }
}
